import useSWR from 'swr'

import { recipeFetcher, RecipeQueryInput, RecipeQueryResponse } from '@library/api-menu-experience'
import { menuRecipeFetcherV3, MenuRecipeParams } from '@library/menu-service'
import { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { useIngredientCustomisationsEnabled } from 'routes/Menu/hooks/useIngredientCustomisationsEnabled'

export const useFetchRecipe = (
  menuId: string,
  // there is no recipe when the details modal is closed
  recipeUuid: string | null,
  numPortions: number,
  deliveryDate: string,
  shouldFetchOptions = true,
  collectionId?: string,
  spicyRecipeDetailsEnabled = false,
): {
  error: boolean
  loading: boolean
  recipe: MenuRecipe | RecipeQueryResponse | null
  reload: () => void
} => {
  const ingredientCustomisationsEnabled = useIngredientCustomisationsEnabled()

  const getKey = () => {
    if (!recipeUuid || !shouldFetchOptions) return null

    if (spicyRecipeDetailsEnabled) {
      const defaultCustomisationTypes = ['none', 'recipes']

      return {
        menuId,
        recipeId: recipeUuid,
        numPortions,
        collectionId,
        deliveryDate,
        customisationTypes: ingredientCustomisationsEnabled
          ? [...defaultCustomisationTypes, 'ingredients']
          : defaultCustomisationTypes,
      } as RecipeQueryInput
    }

    return {
      menuId,
      recipeId: recipeUuid,
      numPortions,
      collectionId,
      deliveryDate,
      ingredientCustomisationsEnabled,
    } as MenuRecipeParams
  }

  const unifiedFetcher = async (
    key: RecipeQueryInput | MenuRecipeParams,
  ): Promise<MenuRecipe | RecipeQueryResponse> => {
    if (spicyRecipeDetailsEnabled) {
      return recipeFetcher(key as RecipeQueryInput)
    } else {
      return menuRecipeFetcherV3(key as MenuRecipeParams)
    }
  }

  const { data, mutate, isValidating, error } = useSWR(getKey(), unifiedFetcher)

  const reload = () => {
    mutate()
  }

  return {
    error: !!error,
    loading: !data && isValidating,
    recipe: data || null,
    reload,
  }
}
